import React, { useEffect, useState } from "react";
import { User, X, Maximize2 } from "lucide-react";
import useCrmApi from "../../../../components/hook/crm-req";
import { ContactItem } from "../types";
import { formatDateTime } from "../../../../utils/function";
import { TicketProps } from "../../ticket/types";
import { Conversation } from "../../conversation/types";
import BasicInfoTab from "./basic-info-tab";
import TicketsTab from "./ticket";
import ConversationsTab from "./convo";
import FeedbackTab from "./feedback";
import DealsTab from "./deals";


type Tab = "basic" | "tickets" | "conversations" | "feedback" | "deals";

interface CustomerDetailProps {
  id?: string;
  onClose?: () => void;
  isDrawer?: boolean;
  onExpand?: () => void;
}

const CustomerDetail: React.FC<CustomerDetailProps> = ({
  id,
  onClose,
  isDrawer = false,
  onExpand,
}) => {
  const [customer, setCustomer] = useState<ContactItem | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("basic");
  const [tickets, setTickets] = useState<TicketProps[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [conversationsLoading, setConversationsLoading] = useState(false);

  const token = localStorage.getItem("authToken");

  const { makeRequest: getCustomer, loading } = useCrmApi(
    `/contacts/${id}`,
    "GET",
    { Authorization: `Bearer ${token}` }
  );
  const { makeRequest: getConversations } = useCrmApi(
    `/contacts/${id}/conversations`,
    "GET",
    { Authorization: `Bearer ${token}` }
  );
  const { makeRequest: getTickets } = useCrmApi(`/contacts/${id}/tickets`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    const fetchCustomer = async () => {
      const [response] = await getCustomer();
      setCustomer(response);
    };
    fetchCustomer();
  }, [id]);

  useEffect(() => {
    if (activeTab === "tickets" && tickets.length === 0) {
      const fetchTickets = async () => {
        setTicketsLoading(true);
        try {
          const [response] = await getTickets();
          setTickets(response?.results || response || []);
        } finally {
          setTicketsLoading(false);
        }
      };
      fetchTickets();
    }
    if (activeTab === "conversations" && conversations.length === 0) {
      const fetchConversations = async () => {
        setConversationsLoading(true);
        try {
          const [response] = await getConversations();
          setConversations(response?.results || response || []);
        } finally {
          setConversationsLoading(false);
        }
      };
      fetchConversations();
    }
  }, [activeTab]);

  const fullName =
    `${customer?.first_name || customer?.firstName || ""} ${customer?.last_name || customer?.lastName || ""
      }`.trim() || "Unnamed Customer";

  const lastActive = customer?.updatedAt || customer?.updated_at;

  const TABS: { key: Tab; label: string }[] = [
    { key: "basic", label: "Basic Info" },
    { key: "tickets", label: "Tickets" },
    { key: "conversations", label: "Conversations" },
    { key: "deals", label: "Deals" },
    { key: "feedback", label: "Feedback" },
  ];

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <DrawerHeader
          name="Loading…"
          subtitle=""
          onClose={onClose}
          onExpand={onExpand}
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-3" />
            <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">Loading customer details…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="flex flex-col h-full">
        <DrawerHeader
          name="Not found"
          subtitle=""
          onClose={onClose}
          onExpand={onExpand}
        />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">Customer not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      {/* Header */}
      <DrawerHeader
        name={fullName}
        subtitle={
          lastActive
            ? `Last Message · ${formatDateTime(lastActive)}`
            : undefined
        }
        onClose={onClose}
        onExpand={onExpand}
      />

      {/* Tabs */}
      <div className="border-b border-gray-100 dark:border-gray-800 px-5">
        {!onExpand && (
          <div className="flex gap-0">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === tab.key
                  ? "border-indigo-600 text-indigo-600 dark:text-indigo-400"
                  : "border-transparent text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === "basic" && (
          <BasicInfoTab customer={customer} />
        )}
        {activeTab === "tickets" && (
          <TicketsTab
            customer={customer}
            tickets={tickets}
            loading={ticketsLoading}
            onRefresh={() => {
              setTickets([]);
              setActiveTab("basic");
              setTimeout(() => setActiveTab("tickets"), 0);
            }}
          />
        )}
        {activeTab === "conversations" && (
          <ConversationsTab

            conversations={conversations}
            loading={conversationsLoading}
          />
        )}
        {activeTab === "deals" && (
          <DealsTab contactId={id} />
        )}
        {activeTab === "feedback" && (
          <FeedbackTab contactId={id} />
        )}
      </div>
    </div>
  );
};

// ── DrawerHeader ──────────────────────────────────────────────────────────────

const DrawerHeader: React.FC<{
  name: string;
  subtitle?: string;
  onClose?: () => void;
  onExpand?: () => void;
}> = ({ name, subtitle, onClose, onExpand }) => (
  <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center shrink-0">
        <User size={18} className="text-indigo-500 dark:text-indigo-400" />
      </div>
      <div>
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{name}</p>
        {subtitle && (
          <p className="text-xs text-indigo-500 dark:text-indigo-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    <div className="flex items-center gap-1 shrink-0">
      {onExpand && (
        <button
          onClick={onExpand}
          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          title="Expand"
        >
          <Maximize2 size={15} />
        </button>
      )}
      {onClose && (
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          title="Close"
        >
          <X size={15} />
        </button>
      )}
    </div>
  </div>
);

export default CustomerDetail;